import axios from 'axios';
import { API_BASE_URL } from '../config';

// Helper function to log API calls in development
const logApiCall = (method, endpoint, data = null) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(`SuperAdmin API ${method}: ${endpoint}`, data ? { data } : '');
  }
};

// Create axios instance for superadmin API calls
const superadminAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/superadmin`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token to requests
superadminAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
superadminAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle token expiration - but be more careful about redirects
    if (error.response && error.response.status === 401) {
      // Only redirect if we're sure it's a token issue and not already on login page
      if (!window.location.pathname.includes('/login')) {
        const token = localStorage.getItem('token');
        if (!token) {
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        // If token exists but got 401, let the component handle the error
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard stats
export const getDashboardStats = () => {
  logApiCall('GET', '/dashboard/stats');
  return superadminAPI.get('/dashboard/stats');
};

// Company admin management
export const getAllCompanyAdmins = () => {
  logApiCall('GET', '/company-admins');
  return superadminAPI.get('/company-admins');
};

export const getCompanyAdminDetails = (id) => {
  if (!id) {
    console.error('Invalid company admin ID provided');
    return Promise.reject(new Error('Invalid company admin ID'));
  }
  
  logApiCall('GET', `/company-admins/${id}`);
  return superadminAPI.get(`/company-admins/${id}`)
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Empty response received from server');
      }

      if (!response.data.admin || typeof response.data.admin !== 'object') {
        throw new Error('Invalid admin data in response');
      }

      return response;
    })
    .catch(error => {
      throw error;
    });
};

export const createCompanyAdmin = (data) => {
  logApiCall('POST', '/company-admins', data);
  return superadminAPI.post('/company-admins', data);
};

export const updateCompanyAdminStatus = (id, status) => {
  logApiCall('PUT', `/company-admins/${id}/status`, { status });
  return superadminAPI.put(`/company-admins/${id}/status`, { status });
};

export const updateCompanyAdmin = (id, data) => {
  logApiCall('PUT', `/company-admins/${id}`, data);
  return superadminAPI.put(`/company-admins/${id}`, data);
};

export const exportCompanyAdminCredentials = (id) => {
  logApiCall('GET', `/company-admins/${id}/credentials`);
  return superadminAPI.get(`/company-admins/${id}/credentials`, { responseType: 'blob' });
};

// Site management
export const createSiteForCompany = (data) => {
  logApiCall('POST', '/sites', data);
  return superadminAPI.post('/sites', data);
};

export const getAdminActiveCheckins = (page = 1, limit = 100) => {
  logApiCall('GET', '/checkins/admin/checkins/active', { page, limit });
  return superadminAPI.get(`${API_BASE_URL}/api/checkins/admin/checkins/active`, { params: { page, limit } });
};

export const forceCheckout = (payload, isFormData = false) => {
  logApiCall('PUT', '/checkins/admin/checkins/force-checkout', isFormData ? 'FormData' : payload);
  const config = isFormData ? {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  } : {};
  return superadminAPI.put(`${API_BASE_URL}/api/checkins/admin/checkins/force-checkout`, payload, config);
};

const superadminAPIService = {
  getDashboardStats,
  getAllCompanyAdmins,
  getCompanyAdminDetails,
  createCompanyAdmin,
  updateCompanyAdminStatus,
  updateCompanyAdmin,
  exportCompanyAdminCredentials,
  createSiteForCompany
  , getAdminActiveCheckins, forceCheckout
};

export default superadminAPIService;
